import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle, XCircle, Eye, Edit, Archive, Calculator, Percent, Euro,
  ChevronDown, ChevronUp, Settings, Plus, AlertTriangle, Trash2, Clock,
  RefreshCw, Copy
} from 'lucide-react';
import { COEF_COLOR_CLASSES, PARAM_COLOR_BADGES, ACTION_COLOR_BADGES } from './constants';

export default function ActifsTab({
  parametresActifs,
  expandedSections,
  toggleSection,
  setExpandedSections,
  customParams,
  restrictions,
  handlePreview,
  handleEdit,
  handleArchive,
  openCustomParamDialog,
  handleToggleCustomParam,
  handleDeleteCustomParam,
  formatCustomParamValue,
  getParamTypeInfo,
  openRestrictionDialog,
  handleToggleRestriction,
  handleDeleteRestriction,
  getRestrictionTypeInfo,
  getRestrictionActionInfo,
  copyToClipboard,
  openNewDialog,
}) {
  return (
    <>
            {parametresActifs ? (
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-white">
                      <CheckCircle className="h-6 w-6" />
                      <div>
                        <h2 className="text-xl font-bold">Paramètres Actuellement Actifs</h2>
                        <p className="text-blue-100 text-sm">Utilisés pour tous les calculs BATICO</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handlePreview(parametresActifs)} 
                        variant="secondary" 
                        size="sm"
                        className="bg-white/20 hover:bg-white/30 text-white border-0"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Aperçu
                      </Button>
                      <Button 
                        onClick={() => handleEdit(parametresActifs)} 
                        variant="secondary" 
                        size="sm"
                        className="bg-white/20 hover:bg-white/30 text-white border-0"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Modifier
                      </Button>
                      {!parametresActifs.is_archived && (
                        <Button 
                          onClick={() => handleArchive(parametresActifs.id)} 
                          variant="secondary" 
                          size="sm"
                          className="bg-white/20 hover:bg-white/30 text-white border-0"
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Archiver
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <CardContent className="p-6 space-y-6">
                  {/* Coefficients Section */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleSection('coefficients')}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Calculator className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-slate-800">Coefficients RCD Solidaire</span>
                      </div>
                      {expandedSections.coefficients ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    {expandedSections.coefficients && (
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                          { label: 'Gros Œuvre', value: parametresActifs.coef_gros_oeuvre, color: 'blue' },
                          { label: 'Second Œuvre', value: parametresActifs.coef_second_oeuvre, color: 'green' },
                          { label: 'Prof. Intellectuels', value: parametresActifs.coef_intellectuels, color: 'purple' },
                          { label: 'Travaux Publics', value: parametresActifs.coef_travaux_publics, color: 'amber' }
                        ].map((item, idx) => {
                          const colors = COEF_COLOR_CLASSES[item.color] || COEF_COLOR_CLASSES.blue;
                          return (
                            <div key={idx} className={`${colors.bg} rounded-lg p-4 border ${colors.border}`}>
                              <p className="text-slate-600 text-sm mb-1">{item.label}</p>
                              <p className={`text-2xl font-bold ${colors.text} font-mono`}>
                                {item.value.toFixed(6)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Remises Section */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleSection('remises')}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Percent className="h-5 w-5 text-green-600" />
                        <span className="font-semibold text-slate-800">Remises Appliquées</span>
                      </div>
                      {expandedSections.remises ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    {expandedSections.remises && (
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                          { label: 'Gros Œuvre', value: parametresActifs.remise_gros_oeuvre },
                          { label: 'Second Œuvre', value: parametresActifs.remise_second_oeuvre },
                          { label: 'PIB', value: parametresActifs.remise_intellectuels },
                          { label: 'Travaux Publics', value: parametresActifs.remise_travaux_publics }
                        ].map((item, idx) => (
                          <div key={idx} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                            <p className="text-slate-600 text-sm mb-1">Remise {item.label}</p>
                            <p className={`text-2xl font-bold font-mono ${item.value > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                              {(item.value * 100).toFixed(1)}%
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Seuil CA Section */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleSection('seuil')}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Euro className="h-5 w-5 text-amber-600" />
                        <span className="font-semibold text-slate-800">Seuil de Chiffre d&apos;Affaires</span>
                      </div>
                      {expandedSections.seuil ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    {expandedSections.seuil && (
                      <div className="p-4 flex flex-col md:flex-row gap-4">
                        <div className="flex-1 bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <p className="text-slate-600 text-sm mb-1">Montant du seuil</p>
                          <p className="text-2xl font-bold text-amber-600 font-mono">
                            {parametresActifs.seuil_ca.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                        <div className="flex-1 bg-slate-50 rounded-lg p-4 border border-slate-200 flex items-center justify-between">
                          <div>
                            <p className="text-slate-600 text-sm mb-1">Restriction active</p>
                            <p className="text-lg font-semibold">
                              {parametresActifs.seuil_ca_actif ? 'Oui' : 'Non'}
                            </p>
                          </div>
                          {parametresActifs.seuil_ca_actif ? (
                            <Badge className="bg-orange-700 text-white border border-orange-400 font-semibold">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Activé
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-700 text-white border border-slate-400 font-semibold">
                              <XCircle className="h-3 w-3 mr-1" />
                              Désactivé
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Paramètres Personnalisés Section */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleSection('custom')}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Settings className="h-5 w-5 text-purple-600" />
                        <span className="font-semibold text-slate-800">Paramètres Personnalisés</span>
                        <Badge className="bg-purple-700 text-white font-semibold">
                          {customParams.filter(p => p.is_active).length} actif(s)
                        </Badge>
                      </div>
                      {expandedSections.custom ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    {expandedSections.custom && (
                      <div className="p-4 space-y-4">
                        {/* Bouton Ajouter */}
                        <div className="flex justify-end">
                          <Button
                            onClick={() => openCustomParamDialog()}
                            size="sm"
                            className="bg-purple-700 hover:bg-purple-700"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Ajouter un paramètre
                          </Button>
                        </div>

                        {/* Liste des paramètres personnalisés */}
                        {customParams.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <Settings className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>Aucun paramètre personnalisé</p>
                            <p className="text-sm">Ajoutez des taxes, contributions, frais...</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {customParams.map(param => {
                              const typeInfo = getParamTypeInfo(param.type);
                              return (
                                <div
                                  key={param.id}
                                  className={`rounded-lg p-4 border-2 transition-all ${
                                    param.is_active 
                                      ? 'bg-white border-purple-200 shadow-sm' 
                                      : 'bg-slate-50 border-slate-200 opacity-60'
                                  }`}
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xl">{typeInfo.icon}</span>
                                      <div>
                                        <h4 className="font-semibold text-slate-800">{param.nom}</h4>
                                        <Badge className={`${PARAM_COLOR_BADGES[typeInfo.color] || 'bg-gray-700 text-white'} text-xs`}>
                                          {typeInfo.label}
                                        </Badge>
                                      </div>
                                    </div>
                                    {param.is_active ? (
                                      <Badge className="bg-green-700 text-white font-semibold">Actif</Badge>
                                    ) : (
                                      <Badge className="bg-slate-700 text-white font-semibold">Inactif</Badge>
                                    )}
                                  </div>
                                  
                                  <div className="text-2xl font-bold text-purple-600 font-mono mb-2">
                                    {formatCustomParamValue(param)}
                                  </div>
                                  
                                  {param.description && (
                                    <p className="text-xs text-slate-500 mb-3">{param.description}</p>
                                  )}
                                  
                                  <div className="flex flex-wrap gap-1 mb-3">
                                    {(param.applicable_a || []).map(cat => (
                                      <Badge key={cat} variant="outline" className="text-xs bg-slate-700 text-white border-slate-800 font-semibold">
                                        {cat.replace('_', ' ')}
                                      </Badge>
                                    ))}
                                  </div>
                                  
                                  <div className="flex gap-2 pt-2 border-t">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="flex-1 h-7 text-xs"
                                      onClick={() => openCustomParamDialog(param)}
                                    >
                                      <Edit className="h-3 w-3 mr-1" />
                                      Modifier
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className={`h-7 text-xs ${param.is_active ? 'text-orange-600' : 'text-green-600'}`}
                                      onClick={() => handleToggleCustomParam(param)}
                                    >
                                      {param.is_active ? 'Désactiver' : 'Activer'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 w-7 p-0 text-red-600"
                                      onClick={() => handleDeleteCustomParam(param.id)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section Restrictions */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden mt-4">
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, restrictions: !prev.restrictions }))}
                      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <span className="font-semibold text-slate-800">Restrictions & Règles Métier</span>
                        <Badge variant="outline" className="bg-red-700 text-white border-red-900 font-semibold">
                          {restrictions.filter(r => r.is_active).length} active(s)
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openRestrictionDialog(); }}
                          className="bg-red-600 hover:bg-red-700 text-white h-8"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter
                        </Button>
                        {expandedSections.restrictions ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
                      </div>
                    </button>
                    
                    {expandedSections.restrictions && (
                      <div className="p-4 bg-white border-t border-slate-100">
                        {restrictions.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <AlertTriangle className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                            <p>Aucune restriction définie</p>
                            <p className="text-sm text-slate-400 mt-1">Les restrictions permettent de contrôler l&apos;accès et les conditions d&apos;utilisation</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {restrictions.map(restriction => {
                              const typeInfo = getRestrictionTypeInfo(restriction.type);
                              const actionInfo = getRestrictionActionInfo(restriction.action);
                              return (
                                <div 
                                  key={restriction.id} 
                                  className={`p-4 rounded-lg border ${restriction.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <span className="text-2xl">{typeInfo.icon}</span>
                                      <div>
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-semibold text-slate-800">{restriction.nom}</span>
                                          <Badge className={`text-xs ${ACTION_COLOR_BADGES[actionInfo.color] || 'bg-gray-700 text-white'}`}>
                                            {actionInfo.label}
                                          </Badge>
                                          {!restriction.is_active && (
                                            <Badge variant="outline" className="text-xs text-slate-500">Inactif</Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-slate-600">{typeInfo.label}: {restriction.condition || restriction.valeur}</p>
                                        {restriction.message && (
                                          <p className="text-xs text-slate-500 mt-1 italic">&quot;{restriction.message}&quot;</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 w-7 p-0"
                                        onClick={() => openRestrictionDialog(restriction)}
                                      >
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className={`h-7 text-xs ${restriction.is_active ? 'text-orange-600' : 'text-green-600'}`}
                                        onClick={() => handleToggleRestriction(restriction)}
                                      >
                                        {restriction.is_active ? 'Désactiver' : 'Activer'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 w-7 p-0 text-red-600"
                                        onClick={() => handleDeleteRestriction(restriction.id)}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Metadata */}
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500 pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Créé le: {new Date(parametresActifs.created_at).toLocaleString('fr-FR')}
                    </div>
                    <div className="flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" />
                      Modifié le: {new Date(parametresActifs.updated_at).toLocaleString('fr-FR')}
                    </div>
                    <button
                      onClick={() => copyToClipboard(parametresActifs.id)}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <Copy className="h-3 w-3" />
                      ID: {parametresActifs.id?.substring(0, 8)}...
                    </button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="py-16 text-center">
                  <AlertTriangle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Aucun paramètre actif</h3>
                  <p className="text-slate-600 mb-6">
                    Créez un nouveau jeu de paramètres pour commencer à utiliser BATICO.
                  </p>
                  <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Créer des paramètres
                  </Button>
                </CardContent>
              </Card>
            )}
    </>
  );
}
